/**
 * CalibrationQuestion — pregunta de 1-tap al final del guided flow.
 *
 * "Si tuvieras que ceder algo hoy, ¿qué cederías?"
 * Al responder, mueve esa dim al final del ranking de priorities
 * y recalcula recommendations. Es un momento wow-humano.
 */
import React, { useState } from 'react';
import { HelpCircle, Check } from 'lucide-react';
import { api } from '../../lib/api';
import advisor from '../../lib/advisor';

const OPTIONS = [
  { key: 'precio', label: 'El precio', hint: 'Estoy dispuesto a estirarme un poco' },
  { key: 'ubicacion', label: 'La ubicación', hint: 'La zona ideal puede esperar' },
  { key: 'espacio', label: 'El espacio', hint: 'Con un poco menos me acomodo' },
  { key: 'tiempo', label: 'El tiempo (commute)', hint: 'Los minutos extra no son un drama' },
];

export default function CalibrationQuestion({ onAnswered, className = '' }) {
  const [answered, setAnswered] = useState(null);
  const [loading, setLoading] = useState(false);

  const handle = async (key) => {
    if (loading || answered) return;
    setLoading(true);
    try {
      const ctxId = advisor.getActiveContextId();
      if (ctxId) {
        await api.submitCalibration({ context_id: ctxId, concession: key });
      }
      setAnswered(key);
      onAnswered?.(key);
    } catch {
      /* silencioso */
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`bg-gradient-to-br from-habita-mint/50 to-habita/5 rounded-2xl border border-habita/20 p-5 ${className}`}
      data-testid="calibration-question"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-full bg-habita text-white grid place-items-center shrink-0">
          <HelpCircle className="w-4 h-4" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-habita font-semibold">
            Momento de calibración
          </p>
          <h3 className="font-serif text-lg font-bold text-habita-dark leading-tight">
            Si tuvieras que ceder algo hoy, ¿qué cederías?
          </h3>
        </div>
      </div>
      <p className="text-xs text-gray-600 mb-3">
        Tu respuesta me ayuda a afinar tus recomendaciones en tiempo real. Nada es permanente — puedes cambiar de idea cuando quieras.
      </p>
      <div className="grid grid-cols-2 gap-2">
        {OPTIONS.map((o) => {
          const isSel = answered === o.key;
          return (
            <button
              key={o.key}
              onClick={() => handle(o.key)}
              disabled={loading || Boolean(answered)}
              data-testid={`calibration-option-${o.key}`}
              className={
                (isSel
                  ? 'bg-habita text-white border-habita'
                  : answered
                    ? 'opacity-40 border-gray-200 bg-white'
                    : 'bg-white border-gray-200 hover:border-habita hover:bg-habita-mint/30 text-habita-dark') +
                ' rounded-xl border-2 p-3 text-left transition-all disabled:cursor-not-allowed'
              }
            >
              <p className="font-semibold text-sm flex items-center gap-1">
                {isSel && <Check className="w-3.5 h-3.5" />}
                {o.label}
              </p>
              <p className={`text-[11px] mt-0.5 ${isSel ? 'text-white/90' : 'text-gray-500'}`}>
                {o.hint}
              </p>
            </button>
          );
        })}
      </div>
      {answered && (
        <p className="mt-3 text-xs text-habita-dark font-medium" data-testid="calibration-confirmed">
          Perfecto. Voy a rebajar el peso de <span className="font-semibold">{OPTIONS.find(x => x.key === answered)?.label.toLowerCase()}</span> en tu próximo ranking.
        </p>
      )}
    </div>
  );
}
